import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom'; // 1. Router Imports
import Privacy from './Privacy';
import GeneralPrivacy from './GeneralPrivacy';
import './App.css';
import logo from './assets/logo.png';
import booktruckIcon from './assets/booktruck-icon.png';
import aiWorkshopImg from './assets/ai-workshop.jpg';
import aiConferenceImg from './assets/ai-conference.jpg';
import {
  FaBars, FaTimes, FaTaxi, FaBus,
  FaEnvelope, FaPhoneAlt, FaLaptopCode, FaGlobe,
  FaMobileAlt, FaPlug, FaNetworkWired, FaProjectDiagram,
  FaRobot, FaBrain, FaChalkboardTeacher, FaCogs,
  FaFacebook, FaMotorcycle, FaBoxOpen,
  FaPaperPlane, FaCheckCircle,
  FaStar, FaQuoteLeft, FaMapMarkerAlt,
  FaGooglePlay, FaShieldAlt, FaMapPin, FaBell, FaArrowRight, FaLinkedin,
  FaHardHat, FaTh, FaPaintRoller, FaLayerGroup
} from 'react-icons/fa';

// --- DATA ---
const rotatingItems = [
  { category: 'MISSION',   text: 'Engineering The Future'         },
  { category: 'DIVISION',  text: 'Logistics & Transport'          },
  { category: 'SERVICE',   text: 'Private Taxis & Shuttles'       },
  { category: 'SERVICE',   text: 'Fast Motorbike Delivery'        },
  { category: 'DIVISION',  text: 'StopNext Technologies'          },
  { category: 'SERVICE',   text: 'Web & Mobile Development'       },
  { category: 'SERVICE',   text: 'API & Enterprise Integration'   },
  { category: 'DIVISION',  text: 'StopNext Construction'          },
  { category: 'SERVICE',   text: 'House Builds & Renovations'     },
  { category: 'SERVICE',   text: 'AI Solutions & Automation'      },
  { category: 'VALUES',    text: 'Efficiency · Integrity · Excellence' },
];

const testimonials = [
  {
    text: "The AI workshop completely changed how our team works day-to-day. Tasks that used to eat up hours are now automated. StopNext made AI feel practical and approachable — not intimidating at all.",
    name: "Sarah M.",
    role: "Operations Director",
    company: "Harare Retail Co.",
    initials: "SM",
  },
  {
    text: "We brought StopNext in to train our entire IT team on AI tools. Every session was hands-on and immediately applicable. Within two weeks we had automated three of our core internal workflows.",
    name: "David K.",
    role: "Head of IT",
    company: "Bulawayo Manufacturing Ltd",
    initials: "DK",
  },
  {
    text: "The AI strategy consulting gave us a roadmap we actually followed. They didn't just present theory — they showed us exactly where AI fits in our business and got us moving fast.",
    name: "Tendai R.",
    role: "Chief Executive",
    company: "ZimTech Solutions",
    initials: "TR",
  },
];

// === SCROLL UTILITIES ===
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.unobserve(el); } },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}

function AnimateIn({ children, as: Tag = 'div', className = '', delay = 0, style = {} }) {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      className={`anim-up ${inView ? 'anim-in' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
    >
      {children}
    </Tag>
  );
}

function CountUp({ value, className }) {
  const num = parseInt(value);
  const countable = !isNaN(num) && num <= 100;
  const [display, setDisplay] = useState(value);
  const [ref, inView] = useInView();
  useEffect(() => {
    if (!inView) return;
    if (!countable) { setDisplay(value); return; }
    const suffix = value.replace(/[0-9]/g, '');
    let c = 0;
    const id = setInterval(() => {
      c = Math.min(c + 1, num);
      setDisplay(c + suffix);
      if (c >= num) clearInterval(id);
    }, 60);
    return () => clearInterval(id);
  }, [inView, countable, num, value]);
  return <span ref={ref} className={className}>{display}</span>;
}

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%?[]{}=+*<>';

// Text-scramble / decode component
function ScrambleText({ text }) {
  const [display, setDisplay] = useState(text);

  useEffect(() => {
    let step = 0;
    const maxSteps = 22;
    const id = setInterval(() => {
      step++;
      setDisplay(
        text.split('').map((char, i) => {
          if (char === ' ' || char === '·' || char === '&') return char;
          if (i < (step / maxSteps) * text.length) return char;
          return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        }).join('')
      );
      if (step >= maxSteps) {
        clearInterval(id);
        setDisplay(text);
      }
    }, 38);
    return () => clearInterval(id);
  }, [text]);

  return <span className="scramble-text">{display}</span>;
}

// === CONTACT FORM ===
function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [status, setStatus] = useState('idle');

  const encode = (data) =>
    Object.keys(data).map(k => encodeURIComponent(k) + '=' + encodeURIComponent(data[k])).join('&');

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({ 'form-name': 'contact', ...formData }),
    })
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  };

  if (status === 'success') {
    return (
      <div className="form-success">
        <FaCheckCircle className="form-success-icon" />
        <h3>Transmission Received</h3>
        <p>Thank you — we'll respond within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field">
      <input type="hidden" name="form-name" value="contact" />
      <input type="hidden" name="bot-field" />
      <div className="contact-form-grid">
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input type="text" name="name" className="form-input" placeholder="Your full name" value={formData.name} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label className="form-label">Email Address *</label>
          <input type="email" name="email" className="form-input" placeholder="your@email.com" value={formData.email} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input type="tel" name="phone" className="form-input" placeholder="+263 ..." value={formData.phone} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label className="form-label">Service Interest *</label>
          <select name="service" className="form-input form-select" value={formData.service} onChange={handleChange} required>
            <option value="">— Select a service —</option>
            <option value="Logistics & Transport">Logistics &amp; Transport</option>
            <option value="Motorbike Delivery">Motorbike Delivery</option>
            <option value="Web & Mobile Development">Web &amp; Mobile Development</option>
            <option value="API & Enterprise Integration">API &amp; Enterprise Integration</option>
            <option value="AI Solutions & Automation">AI Solutions &amp; Automation</option>
            <option value="House Construction">House Construction</option>
            <option value="Professional Tiling">Professional Tiling</option>
            <option value="Modern Ceilings">Modern Ceilings</option>
            <option value="Renovations & Painting">Renovations &amp; Painting</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="form-group form-full">
          <label className="form-label">Message *</label>
          <textarea name="message" className="form-input form-textarea" placeholder="Tell us about your project or enquiry..." value={formData.message} onChange={handleChange} required />
        </div>
      </div>
      {status === 'error' && (
        <p className="form-error">Something went wrong. Please try again or email us directly.</p>
      )}
      <button type="submit" className="form-submit-btn" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending...' : <><FaPaperPlane style={{ marginRight: '10px' }} />Send Message</>}
      </button>
    </form>
  );
}

// === SHARED CAROUSEL HOOK ===
function useCarousel(total, ms = 5000) {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % total), ms);
    return () => clearInterval(t);
  }, [total, ms]);
  return [slide, setSlide];
}

// === TECH SHOWCASE ===
const TECH_CODE = [
  { t: 'import { useTruck } from "@booktruck/sdk";', c: 'imp'   },
  { t: 'import { MapView, Pin } from "sn-maps";',   c: 'imp'   },
  { t: '',                                           c: 'blank' },
  { t: 'export function TrackShipment({ id }) {',    c: 'fn'    },
  { t: '  const { coords, eta } = useTruck(id);',   c: 'body'  },
  { t: '  return (',                                 c: 'body'  },
  { t: '    <MapView center={coords}>',              c: 'jsx'   },
  { t: '      <Pin label={"ETA " + eta} />',          c: 'jsx'   },
  { t: '    </MapView>',                             c: 'jsx'   },
  { t: '  );',                                       c: 'body'  },
  { t: '}',                                          c: 'fn'    },
];

const BUILD_LOG = [
  { time: '14:02:01', msg: 'Repo initialised'        },
  { time: '14:02:04', msg: 'API routes wired'        },
  { time: '14:02:07', msg: 'DB migration applied'    },
  { time: '14:02:09', msg: 'Tests passing (47/47)'   },
  { time: '14:02:11', msg: 'Bundle optimised'        },
  { time: '14:02:13', msg: 'Deployed to production'  },
  { time: '14:02:15', msg: 'Lighthouse: 98/100'      },
  { time: '14:02:17', msg: 'Client signed off'       },
];

const TECH_CAPTIONS = [
  { label: 'WEB & MOBILE · REACT',             cap: 'Modern Apps Built for Scale'          },
  { label: 'API & ENTERPRISE INTEGRATION',     cap: 'Connecting Every Layer of Your Stack' },
  { label: 'SYSTEM DESIGN & ARCHITECTURE',     cap: 'Blueprints Engineered to Last'        },
  { label: 'CUSTOM SOFTWARE · BUILD OUTPUT',   cap: 'Code That Ships on Time'              },
];

const ARCH_TIERS = [
  { label: 'FRONTEND',         sub: 'React',                        y: 18,  w: 420, x: 60  },
  { label: 'API / BACKEND',    sub: 'Java · Spring Boot',           y: 88,  w: 320, x: 110 },
  { label: 'MESSAGING',        sub: 'Apache Pulsar',                y: 158, w: 240, x: 150 },
  { label: 'DATABASE / CLOUD', sub: 'PostgreSQL · Oracle Cloud',    y: 228, w: 180, x: 180 },
];

const TECH_API_PATHS = [
  'M 136 150 L 200 150',
  'M 312 108 L 390 68',
  'M 312 130 L 392 124',
  'M 312 170 L 392 176',
  'M 312 192 L 390 232',
];

function TechShowcase() {
  const [slide, setSlide] = useCarousel(4);
  const [codeIdx, setCodeIdx] = useState(0);
  const [buildTop, setBuildTop] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCodeIdx(i => (i + 1) % TECH_CODE.length), 1200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setBuildTop(i => (i + 1) % BUILD_LOG.length), 1800);
    return () => clearInterval(t);
  }, []);

  const visibleBuild = Array.from({ length: 5 }, (_, i) =>
    BUILD_LOG[(buildTop + i) % BUILD_LOG.length]
  );

  return (
    <div className="ai-showcase-wrapper sn-tech-showcase">
      <div className="ai-carousel-frame">
        <span className="awf-corner awf-tl"/><span className="awf-corner awf-tr"/>
        <span className="awf-corner awf-bl"/><span className="awf-corner awf-br"/>
        <div className="awf-scanline"/>
        <div className="acs-slides">

          {/* Slide 0 — code editor */}
          <div className={`acs-slide acs-slide-dark${slide===0?' acs-active':''}`}>
            <div className="acs-terminal">
              <div className="acs-term-hdr">
                <span className="acs-th-dot" style={{background:'#E31837'}}/>
                <span className="acs-th-dot" style={{background:'#F5A523'}}/>
                <span className="acs-th-dot" style={{background:'#00d4ff'}}/>
                <span className="acs-th-ttl">TrackShipment.jsx — StopNext Technologies</span>
              </div>
              <div className="acs-term-body">
                {TECH_CODE.map((line, i) => (
                  <div key={i} className={`acs-term-line acs-code-line${i<=codeIdx?' acs-tl-vis':''}`}>
                    <span className="acs-ln">{String(i+1).padStart(2,'0')}</span>
                    <span className={`acs-code-${line.c}`}>{line.t || ' '}</span>
                    {i===codeIdx && <span className="acs-cursor">▋</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Slide 1 — API integration diagram */}
          <div className={`acs-slide acs-slide-dark${slide===1?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              {TECH_API_PATHS.map((d,i) => (
                <g key={i}>
                  <path d={d} stroke="rgba(0,212,255,0.3)" strokeWidth="1.2" fill="none" strokeDasharray="5 3">
                    <animate attributeName="stroke-dashoffset" from="32" to="0" dur={`${1.0+i*0.18}s`} repeatCount="indefinite"/>
                  </path>
                  <circle r="3" fill="#00d4ff" opacity="0.85">
                    <animateMotion dur={`${1.2+i*0.2}s`} repeatCount="indefinite" path={d}/>
                  </circle>
                </g>
              ))}
              <rect x="36" y="122" width="100" height="56" rx="6" fill="rgba(0,12,24,0.95)" stroke="rgba(0,212,255,0.7)" strokeWidth="1.5"/>
              <text x="86" y="146" textAnchor="middle" fill="#00d4ff" fontSize="8" fontFamily="Orbitron,sans-serif" fontWeight="700">YOUR</text>
              <text x="86" y="161" textAnchor="middle" fill="#00d4ff" fontSize="8" fontFamily="Orbitron,sans-serif" fontWeight="700">APP</text>
              <circle cx="255" cy="150" r="44" fill="rgba(0,18,36,0.95)" stroke="rgba(0,212,255,0.95)" strokeWidth="2"/>
              <circle cx="255" cy="150" r="56" fill="none" stroke="rgba(0,212,255,0.12)">
                <animate attributeName="r" values="44;60;44" dur="2.4s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.4;0;0.4" dur="2.4s" repeatCount="indefinite"/>
              </circle>
              <text x="255" y="146" textAnchor="middle" fill="#00d4ff" fontSize="7.5" fontFamily="Orbitron,sans-serif" fontWeight="900">API</text>
              <text x="255" y="161" textAnchor="middle" fill="#00d4ff" fontSize="7.5" fontFamily="Orbitron,sans-serif" fontWeight="900">GATEWAY</text>
              {[['PAYMENTS',420,56],['SMS/EMAIL',420,122],['MAPS/GPS',420,178],['CRM/ERP',420,244]].map(([lbl,cx,cy]) => (
                <g key={lbl}>
                  <rect x={cx-44} y={cy-22} width="88" height="44" rx="5" fill="rgba(0,8,20,0.95)" stroke="rgba(123,47,255,0.7)" strokeWidth="1.4"/>
                  <text x={cx} y={cy+5} textAnchor="middle" fill="rgba(190,140,255,0.95)" fontSize="7.5" fontFamily="Orbitron,sans-serif" fontWeight="700">{lbl}</text>
                </g>
              ))}
            </svg>
          </div>

          {/* Slide 2 — architecture tiers */}
          <div className={`acs-slide acs-slide-dark${slide===2?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 296" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <marker id="arr" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill="rgba(0,212,255,0.55)"/>
                </marker>
              </defs>
              {ARCH_TIERS.map(({label,sub,y,w,x},i) => (
                <g key={label}>
                  <rect x={x} y={y} width={w} height="52" rx="6"
                    fill={`rgba(0,${10+i*5},${20+i*5},0.95)`}
                    stroke={['rgba(0,212,255,0.8)','rgba(123,47,255,0.7)','rgba(245,165,35,0.65)','rgba(0,200,100,0.6)'][i]}
                    strokeWidth="1.5"/>
                  <text x={x+w/2} y={y+20} textAnchor="middle"
                    fill={['#00d4ff','rgba(190,140,255,0.95)','rgba(245,165,35,0.9)','rgba(0,220,120,0.9)'][i]}
                    fontSize="8.5" fontFamily="Orbitron,sans-serif" fontWeight="900">{label}</text>
                  <text x={x+w/2} y={y+37} textAnchor="middle" fill="rgba(180,200,230,0.55)" fontSize="7.5" fontFamily="Inter,sans-serif">{sub}</text>
                </g>
              ))}
              {['M 270 70 L 270 88','M 270 140 L 270 158','M 270 210 L 270 228'].map((d,i) => (
                <g key={i}>
                  <path d={d} stroke="rgba(0,212,255,0.4)" strokeWidth="1.5" fill="none" markerEnd="url(#arr)"/>
                  <circle r="2.5" fill="rgba(0,212,255,0.8)">
                    <animateMotion dur={`${1.1+i*0.25}s`} repeatCount="indefinite" path={d}/>
                  </circle>
                </g>
              ))}
            </svg>
          </div>

          {/* Slide 3 — build log */}
          <div className={`acs-slide acs-slide-dark${slide===3?' acs-active':''}`}>
            <div className="acs-terminal">
              <div className="acs-term-hdr">
                <span className="acs-th-dot" style={{background:'#E31837'}}/>
                <span className="acs-th-dot" style={{background:'#F5A523'}}/>
                <span className="acs-th-dot" style={{background:'#00d4ff'}}/>
                <span className="acs-th-ttl">sn-deploy — production pipeline</span>
              </div>
              <div className="acs-term-body">
                {BUILD_LOG.map((line,i) => (
                  <div key={i} className={`acs-term-line acs-tc-ok${i<=codeIdx?' acs-tl-vis':''}`}>
                    <span className="acs-prompt acs-tc-cmd">$</span>
                    <span className="asr-log-time">[{line.time}]</span>
                    &nbsp;{line.msg}
                    {i===codeIdx && <span className="acs-cursor">▋</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
        <div className="awf-overlay">
          <div className="acs-dots">
            {[0,1,2,3].map(i=>(
              <button key={i} className={`acs-dot${slide===i?' acs-dot-active':''}`} onClick={()=>setSlide(i)}/>
            ))}
          </div>
          <p className="awf-label">{TECH_CAPTIONS[slide].label}</p>
          <p className="awf-caption">{TECH_CAPTIONS[slide].cap}</p>
        </div>
      </div>

      <div className="ai-showcase-right">
        <div className="asr-panel">
          <p className="awf-label" style={{marginBottom:'18px'}}>DELIVERY RECORD</p>
          {[['15+','Projects Delivered'],['8+','Technologies Mastered'],['100%','On-Time Rate'],['3','Countries Served']].map(([val,lbl]) => (
            <div className="asr-stat" key={lbl}>
              <span className="asr-stat-val">{val}</span>
              <span className="asr-stat-lbl">{lbl}</span>
            </div>
          ))}
        </div>
        <div className="asr-panel">
          <p className="awf-label" style={{marginBottom:'14px'}}>ACTIVE BUILD LOG</p>
          <div className="asr-log">
            {visibleBuild.map((entry,i) => (
              <div key={`${entry.time}-${i}`} className={`asr-log-row${i===0?' asr-log-new':''}`}>
                <span className="asr-log-time">[{entry.time}]</span>
                <span className="asr-log-msg">{entry.msg}</span>
                <span className="asr-log-ok">✓</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// === LOGISTICS SHOWCASE ===
const LG_VEHICLE_ROUTES = [
  { path: 'M 60 80 L 480 80',   dur: '3.5s', color: '#E31837' },
  { path: 'M 480 150 L 60 150', dur: '4.2s', color: '#F5A523' },
  { path: 'M 60 220 L 480 220', dur: '3.0s', color: '#E31837' },
  { path: 'M 120 30 L 120 270', dur: '3.8s', color: '#F5A523' },
  { path: 'M 360 30 L 360 270', dur: '4.5s', color: '#E31837' },
];

const LG_COVERAGE_RINGS = [
  { r: 40,  label: 'CBD',        color: 'rgba(227,24,55,0.9)'  },
  { r: 85,  label: 'SUBURBS',    color: 'rgba(245,165,35,0.7)' },
  { r: 130, label: 'CITY WIDE',  color: 'rgba(245,165,35,0.4)' },
  { r: 175, label: 'INTERCITY',  color: 'rgba(227,24,55,0.25)' },
];

const LG_DELIVERY_PATH = 'M 60 240 C 110 230 140 200 180 180 C 220 160 260 140 300 120 C 340 100 380 75 440 60';

const LG_DISPATCH_LOG = [
  { time: '08:12:01', msg: 'Taxi #14 dispatched'    },
  { time: '08:12:04', msg: 'Pickup — CBD confirmed'  },
  { time: '08:12:07', msg: 'Shuttle #3 en route'     },
  { time: '08:12:10', msg: 'Delivery #88 picked up'  },
  { time: '08:12:14', msg: 'Kombi route B active'    },
  { time: '08:12:17', msg: 'Motorbike #7 arrived'    },
  { time: '08:12:20', msg: 'Parcel delivered ✓'      },
  { time: '08:12:24', msg: 'Fleet: 8 active'         },
];

const LG_CAPTIONS = [
  { label: 'LIVE FLEET · HARARE CBD',   cap: 'Real-Time Vehicle Dispatch'    },
  { label: 'ROUTE COVERAGE · NETWORK',  cap: 'City-Wide Mobility Grid'       },
  { label: 'LAST MILE · ACTIVE ROUTE',  cap: 'Fast Delivery, Every Time'     },
  { label: 'DISPATCH LOG · LIVE FEED',  cap: 'Always Moving, Always On Time' },
];

function LogisticsShowcase() {
  const [slide, setSlide] = useCarousel(4);
  const [lgLogTop, setLgLogTop] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setLgLogTop(i => (i + 1) % LG_DISPATCH_LOG.length), 1800);
    return () => clearInterval(t);
  }, []);

  const visibleLgLog = Array.from({ length: 5 }, (_, i) =>
    LG_DISPATCH_LOG[(lgLogTop + i) % LG_DISPATCH_LOG.length]
  );

  return (
    <div className="ai-showcase-wrapper lg-showcase">
      <div className="ai-carousel-frame lg-frame">
        <span className="awf-corner awf-tl lg-corner"/><span className="awf-corner awf-tr lg-corner"/>
        <span className="awf-corner awf-bl lg-corner"/><span className="awf-corner awf-br lg-corner"/>
        <div className="awf-scanline"/>
        <div className="acs-slides">

          {/* Slide 0 — city fleet map */}
          <div className={`acs-slide acs-slide-dark${slide===0?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              {[120,240,360].map(x => (
                <line key={`gv${x}`} x1={x} y1="20" x2={x} y2="280" stroke="rgba(227,24,55,0.06)" strokeWidth="1"/>
              ))}
              {[80,150,220].map(y => (
                <line key={`gh${y}`} x1="40" y1={y} x2="500" y2={y} stroke="rgba(227,24,55,0.06)" strokeWidth="1"/>
              ))}
              <line x1="40" y1="80" x2="500" y2="80" stroke="rgba(227,24,55,0.2)" strokeWidth="2"/>
              <line x1="40" y1="150" x2="500" y2="150" stroke="rgba(227,24,55,0.2)" strokeWidth="2"/>
              <line x1="40" y1="220" x2="500" y2="220" stroke="rgba(227,24,55,0.2)" strokeWidth="2"/>
              <line x1="120" y1="20" x2="120" y2="280" stroke="rgba(227,24,55,0.2)" strokeWidth="2"/>
              <line x1="240" y1="20" x2="240" y2="280" stroke="rgba(227,24,55,0.2)" strokeWidth="2"/>
              <line x1="360" y1="20" x2="360" y2="280" stroke="rgba(227,24,55,0.2)" strokeWidth="2"/>
              {LG_VEHICLE_ROUTES.map((r, i) => (
                <g key={i}>
                  <circle r="5" fill={r.color} opacity="0.9">
                    <animateMotion dur={r.dur} repeatCount="indefinite" path={r.path}/>
                  </circle>
                  <circle r="10" fill={r.color} opacity="0.18">
                    <animateMotion dur={r.dur} repeatCount="indefinite" path={r.path}/>
                  </circle>
                </g>
              ))}
              <circle cx="240" cy="150" r="18" fill="rgba(5,10,20,0.95)" stroke="rgba(227,24,55,0.9)" strokeWidth="2"/>
              <circle cx="240" cy="150" r="26" fill="none" stroke="rgba(227,24,55,0.2)">
                <animate attributeName="r" values="18;34;18" dur="2s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite"/>
              </circle>
              <text x="240" y="154" textAnchor="middle" fill="rgba(227,24,55,0.9)" fontSize="7" fontFamily="Orbitron,sans-serif" fontWeight="900">HQ</text>
              <text x="270" y="293" textAnchor="middle" fill="rgba(227,24,55,0.35)" fontSize="8" fontFamily="Orbitron,sans-serif">HARARE CBD — ACTIVE FLEET</text>
            </svg>
          </div>

          {/* Slide 1 — coverage zones */}
          <div className={`acs-slide acs-slide-dark${slide===1?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              {LG_COVERAGE_RINGS.map(({ r, label, color }, i) => (
                <g key={i}>
                  <circle cx="270" cy="148" r={r} fill="none" stroke={color} strokeWidth="1.2" strokeDasharray="6 4"/>
                  <circle cx="270" cy="148" r={r} fill="none" stroke={color} strokeWidth="0.5" opacity="0.3">
                    <animate attributeName="r" values={`${r};${r+10};${r}`} dur={`${2+i*0.6}s`} repeatCount="indefinite"/>
                    <animate attributeName="opacity" values="0.3;0;0.3" dur={`${2+i*0.6}s`} repeatCount="indefinite"/>
                  </circle>
                  <text x={270+r+8} y="152" fill={color} fontSize="7" fontFamily="Orbitron,sans-serif" fontWeight="700">{label}</text>
                </g>
              ))}
              <circle cx="270" cy="148" r="12" fill="rgba(227,24,55,0.2)" stroke="rgba(227,24,55,0.9)" strokeWidth="2"/>
              <circle cx="270" cy="148" r="5" fill="#E31837"/>
              <text x="270" y="293" textAnchor="middle" fill="rgba(227,24,55,0.35)" fontSize="8" fontFamily="Orbitron,sans-serif">COVERAGE ZONE — ZIMBABWE</text>
            </svg>
          </div>

          {/* Slide 2 — delivery route */}
          <div className={`acs-slide acs-slide-dark${slide===2?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              {[60,120,180,240,300,360,420,480].map(x => (
                <line key={`v${x}`} x1={x} y1="0" x2={x} y2="300" stroke="rgba(227,24,55,0.04)" strokeWidth="1"/>
              ))}
              {[60,120,180,240].map(y => (
                <line key={`h${y}`} x1="0" y1={y} x2="540" y2={y} stroke="rgba(227,24,55,0.04)" strokeWidth="1"/>
              ))}
              <path d={LG_DELIVERY_PATH} stroke="rgba(227,24,55,0.35)" strokeWidth="2.5" fill="none" strokeDasharray="8 4"/>
              <circle r="6" fill="#E31837" opacity="0.95">
                <animateMotion dur="5s" repeatCount="indefinite" path={LG_DELIVERY_PATH}/>
              </circle>
              <circle r="13" fill="rgba(227,24,55,0.18)" opacity="0.8">
                <animateMotion dur="5s" repeatCount="indefinite" path={LG_DELIVERY_PATH}/>
              </circle>
              <circle cx="60" cy="240" r="7" fill="none" stroke="#F5A523" strokeWidth="2">
                <animate attributeName="r" values="5;13;5" dur="1.8s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="1;0;1" dur="1.8s" repeatCount="indefinite"/>
              </circle>
              <circle cx="60" cy="240" r="4" fill="#F5A523"/>
              <text x="60" y="265" textAnchor="middle" fill="rgba(245,165,35,0.75)" fontSize="7.5" fontFamily="Orbitron,sans-serif">PICKUP</text>
              <circle cx="440" cy="60" r="7" fill="none" stroke="#00d4ff" strokeWidth="2">
                <animate attributeName="r" values="5;13;5" dur="2.2s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="1;0;1" dur="2.2s" repeatCount="indefinite"/>
              </circle>
              <circle cx="440" cy="60" r="4" fill="#00d4ff"/>
              <text x="440" y="85" textAnchor="middle" fill="rgba(0,212,255,0.75)" fontSize="7.5" fontFamily="Orbitron,sans-serif">DELIVERY</text>
              <text x="270" y="292" textAnchor="middle" fill="rgba(227,24,55,0.4)" fontSize="8" fontFamily="Orbitron,sans-serif">LAST-MILE DELIVERY · ETA 12 MIN</text>
            </svg>
          </div>

          {/* Slide 3 — dispatch terminal */}
          <div className={`acs-slide acs-slide-dark${slide===3?' acs-active':''}`}>
            <div className="acs-terminal">
              <div className="acs-term-hdr">
                <span className="acs-th-dot" style={{background:'#E31837'}}/>
                <span className="acs-th-dot" style={{background:'#F5A523'}}/>
                <span className="acs-th-dot" style={{background:'#00d4ff'}}/>
                <span className="acs-th-ttl">dispatch-control — fleet live</span>
              </div>
              <div className="acs-term-body">
                {LG_DISPATCH_LOG.map((line, i) => (
                  <div key={i} className={`acs-term-line${i<=(lgLogTop%LG_DISPATCH_LOG.length)+3?' acs-tl-vis':''}`}
                    style={{color: i===lgLogTop%LG_DISPATCH_LOG.length ? '#E31837' : 'rgba(200,220,255,0.55)'}}>
                    <span className="acs-prompt" style={{color:'#E31837'}}>›</span>
                    <span className="asr-log-time">[{line.time}]</span>
                    &nbsp;{line.msg}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
        <div className="awf-overlay">
          <div className="acs-dots">
            {[0,1,2,3].map(i=>(
              <button key={i} className={`acs-dot${slide===i?' acs-dot-active lg-dot-active':''}`} onClick={()=>setSlide(i)}/>
            ))}
          </div>
          <p className="awf-label" style={{color:'#E31837'}}>{LG_CAPTIONS[slide].label}</p>
          <p className="awf-caption">{LG_CAPTIONS[slide].cap}</p>
        </div>
      </div>

      <div className="ai-showcase-right">
        <div className="asr-panel lg-asr-panel">
          <p className="awf-label" style={{color:'#E31837',marginBottom:'18px'}}>FLEET STATS</p>
          {[['8+','Active Vehicles'],['50+','Routes Covered'],['12min','Avg. Pickup Time'],['1000+','Trips Completed']].map(([val,lbl]) => (
            <div className="asr-stat" key={lbl}>
              <span className="asr-stat-val" style={{color:'#E31837',textShadow:'0 0 12px rgba(227,24,55,0.45)'}}>{val}</span>
              <span className="asr-stat-lbl">{lbl}</span>
            </div>
          ))}
        </div>
        <div className="asr-panel lg-asr-panel">
          <p className="awf-label" style={{color:'#E31837',marginBottom:'14px'}}>LIVE DISPATCH FEED</p>
          <div className="asr-log">
            {visibleLgLog.map((entry,i) => (
              <div key={`${entry.time}-${i}`} className={`asr-log-row${i===0?' asr-log-new':''}`}
                style={i===0?{color:'#E31837'}:{}}>
                <span className="asr-log-time">[{entry.time}]</span>
                <span className="asr-log-msg">{entry.msg}</span>
                <span className="asr-log-ok" style={{color:'#E31837'}}>✓</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// === CONSTRUCTION SHOWCASE ===
const CN_BUILD_LOG = [
  { time: '07:30:01', msg: 'Site cleared & levelled'   },
  { time: '07:30:05', msg: 'Foundation poured'         },
  { time: '07:30:09', msg: 'Frame raised'              },
  { time: '07:30:13', msg: 'Brickwork complete'        },
  { time: '07:30:17', msg: 'Roof installed'            },
  { time: '07:30:21', msg: 'Tiling in progress'        },
  { time: '07:30:25', msg: 'Painting — final coat'     },
  { time: '07:30:29', msg: 'Client handover ✓'         },
];

const CN_CAPTIONS = [
  { label: 'HOUSE CONSTRUCTION · PROGRESS', cap: 'From Foundation to Finish'    },
  { label: 'PROFESSIONAL TILING · INSTALL', cap: 'Flawless Floors and Walls'    },
  { label: 'MODERN CEILINGS · DESIGN',      cap: 'Elegant, Stylish Designs'     },
  { label: 'RENOVATION LOG · LIVE SITE',    cap: 'Refreshing Every Space'       },
];

const CN_BUILD_STAGES = ['FOUNDATION', 'FRAME', 'WALLS', 'ROOF', 'FINISH'];
const CN_COLS = 7, CN_ROWS = 4, CN_TW = 68, CN_TH = 56, CN_GAP = 6;
const CN_TX0 = (540 - (CN_COLS * CN_TW + (CN_COLS - 1) * CN_GAP)) / 2;
const CN_TY0 = (300 - (CN_ROWS * CN_TH + (CN_ROWS - 1) * CN_GAP)) / 2;

function ConstructionShowcase() {
  const [slide, setSlide] = useCarousel(4);
  const [cnLogTop, setCnLogTop] = useState(0);
  const [tileIdx, setTileIdx] = useState(0);
  const [buildStage, setBuildStage] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setBuildStage(i => (i + 1) % 5), 1200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const total = CN_COLS * CN_ROWS;
    const t = setInterval(() => setTileIdx(i => i < total - 1 ? i + 1 : 0), 150);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setCnLogTop(i => (i + 1) % CN_BUILD_LOG.length), 1800);
    return () => clearInterval(t);
  }, []);

  const visibleCnLog = Array.from({ length: 5 }, (_, i) =>
    CN_BUILD_LOG[(cnLogTop + i) % CN_BUILD_LOG.length]
  );

  return (
    <div className="ai-showcase-wrapper cn-showcase">
      <div className="ai-carousel-frame cn-frame">
        <span className="awf-corner awf-tl cn-corner"/><span className="awf-corner awf-tr cn-corner"/>
        <span className="awf-corner awf-bl cn-corner"/><span className="awf-corner awf-br cn-corner"/>
        <div className="awf-scanline"/>
        <div className="acs-slides">

          {/* Slide 0 — House construction stages */}
          <div className={`acs-slide acs-slide-dark${slide===0?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              <line x1="80" y1="248" x2="460" y2="248" stroke="rgba(16,185,129,0.4)" strokeWidth="2"/>
              <rect x="140" y="228" width="260" height="20" rx="2"
                fill="rgba(16,185,129,0.18)" stroke="rgba(16,185,129,0.8)" strokeWidth="1.5"
                style={{opacity: buildStage >= 0 ? 1 : 0, transition: 'opacity 0.4s ease'}}/>
              <rect x="140" y="122" width="30" height="106" rx="1"
                fill="rgba(16,185,129,0.14)" stroke="rgba(16,185,129,0.55)" strokeWidth="1.5"
                style={{opacity: buildStage >= 1 ? 1 : 0, transition: 'opacity 0.4s ease'}}/>
              <rect x="370" y="122" width="30" height="106" rx="1"
                fill="rgba(16,185,129,0.14)" stroke="rgba(16,185,129,0.55)" strokeWidth="1.5"
                style={{opacity: buildStage >= 1 ? 1 : 0, transition: 'opacity 0.4s ease'}}/>
              <rect x="170" y="122" width="200" height="106" rx="0"
                fill="rgba(16,185,129,0.07)" stroke="rgba(16,185,129,0.45)" strokeWidth="1.2"
                style={{opacity: buildStage >= 2 ? 1 : 0, transition: 'opacity 0.4s ease'}}/>
              <polygon points="120,122 270,48 420,122"
                fill="rgba(16,185,129,0.14)" stroke="rgba(16,185,129,0.9)" strokeWidth="2"
                style={{opacity: buildStage >= 3 ? 1 : 0, transition: 'opacity 0.4s ease'}}/>
              <rect x="182" y="150" width="66" height="52" rx="2"
                fill="rgba(0,212,255,0.07)" stroke="rgba(0,212,255,0.38)" strokeWidth="1"
                style={{opacity: buildStage >= 4 ? 1 : 0, transition: 'opacity 0.4s ease'}}/>
              <rect x="292" y="150" width="66" height="52" rx="2"
                fill="rgba(0,212,255,0.07)" stroke="rgba(0,212,255,0.38)" strokeWidth="1"
                style={{opacity: buildStage >= 4 ? 1 : 0, transition: 'opacity 0.4s ease'}}/>
              <rect x="248" y="186" width="44" height="42" rx="2"
                fill="rgba(16,185,129,0.06)" stroke="rgba(16,185,129,0.4)" strokeWidth="1"
                style={{opacity: buildStage >= 4 ? 1 : 0, transition: 'opacity 0.4s ease'}}/>
              {CN_BUILD_STAGES.map((lbl, i) => {
                const cx = 95 + i * 88;
                const active = buildStage >= i;
                return (
                  <g key={lbl}>
                    <circle cx={cx} cy="276" r="5"
                      fill={active ? '#10B981' : 'rgba(16,185,129,0.18)'}
                      stroke={active ? '#10B981' : 'rgba(16,185,129,0.3)'} strokeWidth="1.5"/>
                    <text x={cx} y="292" textAnchor="middle"
                      fill={active ? 'rgba(16,185,129,0.85)' : 'rgba(16,185,129,0.28)'}
                      fontSize="6.5" fontFamily="Orbitron,sans-serif">{lbl}</text>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Slide 1 — Tiling grid */}
          <div className={`acs-slide acs-slide-dark${slide===1?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              {Array.from({length: CN_ROWS}, (_, row) =>
                Array.from({length: CN_COLS}, (_, col) => {
                  const idx = row * CN_COLS + col;
                  const x = CN_TX0 + col * (CN_TW + CN_GAP);
                  const y = CN_TY0 + row * (CN_TH + CN_GAP);
                  const filled = idx <= tileIdx;
                  return (
                    <g key={idx}>
                      <rect x={x} y={y} width={CN_TW} height={CN_TH} rx="2"
                        fill={filled ? 'rgba(16,185,129,0.16)' : 'rgba(0,8,20,0.8)'}
                        stroke={filled ? 'rgba(16,185,129,0.62)' : 'rgba(16,185,129,0.1)'}
                        strokeWidth="1"/>
                      {filled && <>
                        <line x1={x+8} y1={y+8} x2={x+CN_TW-8} y2={y+CN_TH-8}
                          stroke="rgba(16,185,129,0.16)" strokeWidth="0.8"/>
                        <line x1={x+CN_TW-8} y1={y+8} x2={x+8} y2={y+CN_TH-8}
                          stroke="rgba(16,185,129,0.16)" strokeWidth="0.8"/>
                      </>}
                    </g>
                  );
                })
              )}
              <text x="270" y="295" textAnchor="middle" fill="rgba(16,185,129,0.38)" fontSize="8" fontFamily="Orbitron,sans-serif">PROFESSIONAL TILING · ZIMBABWE</text>
            </svg>
          </div>

          {/* Slide 2 — Ceiling panel design */}
          <div className={`acs-slide acs-slide-dark${slide===2?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              {[0,1,2,3].map(row =>
                [0,1,2].map(col => {
                  const x = 50 + col * 150;
                  const y = 18 + row * 66;
                  const d = (row * 3 + col) * 0.22;
                  return (
                    <g key={`${row}-${col}`}>
                      <rect x={x} y={y} width={130} height={52} rx="3"
                        fill="rgba(16,185,129,0.05)" stroke="rgba(16,185,129,0.27)" strokeWidth="1"/>
                      <rect x={x+7} y={y+7} width={116} height={38} rx="2"
                        fill="none" stroke="rgba(16,185,129,0.1)" strokeWidth="0.8"/>
                      <circle cx={x+65} cy={y+26} r="4"
                        fill="rgba(16,185,129,0.22)" stroke="rgba(16,185,129,0.5)" strokeWidth="1">
                        <animate attributeName="opacity" values="0.4;1;0.4" dur={`${1.6+d}s`} repeatCount="indefinite"/>
                      </circle>
                      <circle cx={x+65} cy={y+26} r="9" fill="none" stroke="rgba(16,185,129,0.15)">
                        <animate attributeName="r" values="4;14;4" dur={`${2.0+d}s`} repeatCount="indefinite"/>
                        <animate attributeName="opacity" values="0.5;0;0.5" dur={`${2.0+d}s`} repeatCount="indefinite"/>
                      </circle>
                    </g>
                  );
                })
              )}
              <text x="270" y="295" textAnchor="middle" fill="rgba(16,185,129,0.38)" fontSize="8" fontFamily="Orbitron,sans-serif">MODERN CEILING DESIGN · ZIMBABWE</text>
            </svg>
          </div>

          {/* Slide 3 — Renovation site log */}
          <div className={`acs-slide acs-slide-dark${slide===3?' acs-active':''}`}>
            <div className="acs-terminal">
              <div className="acs-term-hdr">
                <span className="acs-th-dot" style={{background:'#E31837'}}/>
                <span className="acs-th-dot" style={{background:'#10B981'}}/>
                <span className="acs-th-dot" style={{background:'#00d4ff'}}/>
                <span className="acs-th-ttl">site-control — renovation · live</span>
              </div>
              <div className="acs-term-body">
                {CN_BUILD_LOG.map((line, i) => (
                  <div key={i} className={`acs-term-line${i<=(cnLogTop%CN_BUILD_LOG.length)+3?' acs-tl-vis':''}`}
                    style={{color: i===cnLogTop%CN_BUILD_LOG.length ? '#10B981' : 'rgba(200,220,255,0.55)'}}>
                    <span className="acs-prompt" style={{color:'#10B981'}}>›</span>
                    <span className="asr-log-time">[{line.time}]</span>
                    &nbsp;{line.msg}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
        <div className="awf-overlay">
          <div className="acs-dots">
            {[0,1,2,3].map(i=>(
              <button key={i} className={`acs-dot${slide===i?' acs-dot-active cn-dot-active':''}`} onClick={()=>setSlide(i)}/>
            ))}
          </div>
          <p className="awf-label" style={{color:'#10B981'}}>{CN_CAPTIONS[slide].label}</p>
          <p className="awf-caption">{CN_CAPTIONS[slide].cap}</p>
        </div>
      </div>

      <div className="ai-showcase-right">
        <div className="asr-panel cn-asr-panel">
          <p className="awf-label" style={{color:'#10B981',marginBottom:'18px'}}>PROJECT STATS</p>
          {[['20+','Projects Completed'],['5+','Houses Built'],['100%','Client Satisfaction'],['ZWE','Nationwide']].map(([val,lbl]) => (
            <div className="asr-stat" key={lbl}>
              <span className="asr-stat-val" style={{color:'#10B981',textShadow:'0 0 12px rgba(16,185,129,0.45)'}}>{val}</span>
              <span className="asr-stat-lbl">{lbl}</span>
            </div>
          ))}
        </div>
        <div className="asr-panel cn-asr-panel">
          <p className="awf-label" style={{color:'#10B981',marginBottom:'14px'}}>LIVE SITE LOG</p>
          <div className="asr-log">
            {visibleCnLog.map((entry,i) => (
              <div key={`${entry.time}-${i}`} className={`asr-log-row${i===0?' asr-log-new':''}`}
                style={i===0?{color:'#10B981'}:{}}>
                <span className="asr-log-time">[{entry.time}]</span>
                <span className="asr-log-msg">{entry.msg}</span>
                <span className="asr-log-ok" style={{color:'#10B981'}}>✓</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// === BOOKTRUCK SHOWCASE ===
const BT_CHECKS = [
  'National ID Verified',
  "Driver's Licence Confirmed",
  'Vehicle Inspection Passed',
  'Insurance Valid & Current',
  'Criminal Record Clear',
  'Community Rating: 4.8★',
];

const BOOKING_LOG = [
  { time: '10:14:01', msg: 'Booking #2241 created'  },
  { time: '10:14:03', msg: 'Driver matched (4.9★)'  },
  { time: '10:14:05', msg: 'Route calculated'        },
  { time: '10:14:08', msg: 'Driver en route'         },
  { time: '10:14:12', msg: 'Cargo picked up'         },
  { time: '10:14:18', msg: 'GPS tracking active'     },
  { time: '10:14:22', msg: 'ETA: 42 minutes'         },
  { time: '10:14:30', msg: 'Delivery confirmed'      },
];

const BT_CAPTIONS = [
  { label: 'REAL-TIME GPS TRACKING',  cap: 'Your Cargo. Always in Sight.'          },
  { label: 'INSTANT BOOKING FLOW',    cap: 'Book a Truck in Under 3 Minutes'       },
  { label: 'DRIVER VERIFICATION',     cap: 'Every Driver Thoroughly Screened'      },
  { label: 'LIVE SHIPMENT UPDATES',   cap: 'Status at Every Step of the Journey'   },
];

const BT_STEPS = ['BOOK','MATCH','CONFIRM','TRACK','DONE'];
const BT_STEP_SUBS = ['Select cargo','Match driver','Confirm booking','GPS track','Delivered'];
const GPS_ROUTE = 'M 60 240 C 110 240 130 195 170 178 C 210 161 240 140 280 122 C 320 104 365 82 420 60';

function BookTruckShowcase() {
  const [slide, setSlide] = useCarousel(4);
  const [checkIdx, setCheckIdx] = useState(-1);
  const [bookStep, setBookStep] = useState(0);
  const [btLogTop, setBtLogTop] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setCheckIdx(i => i < BT_CHECKS.length - 1 ? i + 1 : -1), 900);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setBookStep(i => (i + 1) % 5), 1100);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setBtLogTop(i => (i + 1) % BOOKING_LOG.length), 1800);
    return () => clearInterval(t);
  }, []);

  const visibleBtLog = Array.from({ length: 5 }, (_, i) =>
    BOOKING_LOG[(btLogTop + i) % BOOKING_LOG.length]
  );

  return (
    <div className="ai-showcase-wrapper bt-showcase">
      <div className="ai-carousel-frame bt-frame">
        <span className="awf-corner awf-tl bt-corner"/><span className="awf-corner awf-tr bt-corner"/>
        <span className="awf-corner awf-bl bt-corner"/><span className="awf-corner awf-br bt-corner"/>
        <div className="awf-scanline"/>
        <div className="acs-slides">

          {/* Slide 0 — GPS map */}
          <div className={`acs-slide acs-slide-dark${slide===0?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              {[60,120,180,240,300,360,420,480].map(x => (
                <line key={`v${x}`} x1={x} y1="0" x2={x} y2="300" stroke="rgba(245,165,35,0.05)" strokeWidth="1"/>
              ))}
              {[60,120,180,240].map(y => (
                <line key={`h${y}`} x1="0" y1={y} x2="540" y2={y} stroke="rgba(245,165,35,0.05)" strokeWidth="1"/>
              ))}
              <path d={GPS_ROUTE} stroke="rgba(245,165,35,0.3)" strokeWidth="2" fill="none" strokeDasharray="8 4"/>
              <circle r="5" fill="#F5A523" opacity="0.95">
                <animateMotion dur="5s" repeatCount="indefinite" path={GPS_ROUTE}/>
              </circle>
              <circle r="10" fill="rgba(245,165,35,0.2)" opacity="0.7">
                <animateMotion dur="5s" repeatCount="indefinite" path={GPS_ROUTE}/>
              </circle>
              <circle cx="60" cy="240" r="7" fill="none" stroke="#F5A523" strokeWidth="2">
                <animate attributeName="r" values="5;13;5" dur="1.8s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="1;0;1" dur="1.8s" repeatCount="indefinite"/>
              </circle>
              <circle cx="60" cy="240" r="4" fill="#F5A523"/>
              <text x="60" y="265" textAnchor="middle" fill="rgba(245,165,35,0.75)" fontSize="7.5" fontFamily="Orbitron,sans-serif">PICKUP</text>
              <circle cx="420" cy="60" r="7" fill="none" stroke="#00d4ff" strokeWidth="2">
                <animate attributeName="r" values="5;13;5" dur="2.2s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="1;0;1" dur="2.2s" repeatCount="indefinite"/>
              </circle>
              <circle cx="420" cy="60" r="4" fill="#00d4ff"/>
              <text x="420" y="85" textAnchor="middle" fill="rgba(0,212,255,0.75)" fontSize="7.5" fontFamily="Orbitron,sans-serif">DELIVERY</text>
              <text x="270" y="292" textAnchor="middle" fill="rgba(245,165,35,0.4)" fontSize="8" fontFamily="Orbitron,sans-serif">HARARE → BULAWAYO</text>
            </svg>
          </div>

          {/* Slide 1 — booking steps */}
          <div className={`acs-slide acs-slide-dark${slide===1?' acs-active':''}`}>
            <svg className="acs-svg" viewBox="0 0 540 280" xmlns="http://www.w3.org/2000/svg">
              {[0,1,2,3].map(i => (
                <line key={i} x1={60+i*108} y1="130" x2={60+(i+1)*108} y2="130"
                  stroke={bookStep>i ? 'rgba(245,165,35,0.8)' : 'rgba(245,165,35,0.2)'}
                  strokeWidth={bookStep>i ? 2 : 1} strokeDasharray={bookStep>i ? '' : '4 3'}/>
              ))}
              {BT_STEPS.map((lbl,i) => {
                const cx = 60 + i*108, active = bookStep >= i;
                return (
                  <g key={lbl}>
                    <circle cx={cx} cy="130" r="30"
                      fill={active ? 'rgba(245,100,0,0.18)' : 'rgba(0,8,20,0.9)'}
                      stroke={active ? '#F5A523' : 'rgba(245,165,35,0.22)'}
                      strokeWidth={active ? 2 : 1.2}/>
                    {active && (
                      <circle cx={cx} cy="130" r="36" fill="none" stroke="rgba(245,165,35,0.2)">
                        <animate attributeName="r" values="30;42;30" dur="1.5s" repeatCount="indefinite"/>
                        <animate attributeName="opacity" values="0.4;0;0.4" dur="1.5s" repeatCount="indefinite"/>
                      </circle>
                    )}
                    <text x={cx} y="134" textAnchor="middle"
                      fill={active ? '#F5A523' : 'rgba(245,165,35,0.28)'}
                      fontSize="7.5" fontFamily="Orbitron,sans-serif" fontWeight="700">{lbl}</text>
                    <text x={cx} y="178" textAnchor="middle"
                      fill={active ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.18)'}
                      fontSize="7" fontFamily="Inter,sans-serif">{BT_STEP_SUBS[i]}</text>
                  </g>
                );
              })}
              <text x="270" y="230" textAnchor="middle" fill="rgba(245,165,35,0.38)" fontSize="8" fontFamily="Orbitron,sans-serif">AVG. BOOKING TIME: 3 MINUTES</text>
            </svg>
          </div>

          {/* Slide 2 — driver verification */}
          <div className={`acs-slide acs-slide-dark${slide===2?' acs-active':''}`}>
            <div className="bt-checklist">
              <p className="bt-check-title">DRIVER VERIFICATION PROTOCOL</p>
              {BT_CHECKS.map((item,i) => (
                <div key={i} className={`bt-check-item${i<=checkIdx?' bt-checked':''}`}>
                  <span className="bt-check-icon">{i<=checkIdx ? '✓' : '○'}</span>
                  <span className="bt-check-text">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Slide 3 — shipment log */}
          <div className={`acs-slide acs-slide-dark${slide===3?' acs-active':''}`}>
            <div className="acs-terminal">
              <div className="acs-term-hdr">
                <span className="acs-th-dot" style={{background:'#E31837'}}/>
                <span className="acs-th-dot" style={{background:'#F5A523'}}/>
                <span className="acs-th-dot" style={{background:'#00d4ff'}}/>
                <span className="acs-th-ttl">booktruck — shipment #2241 · live</span>
              </div>
              <div className="acs-term-body">
                {BOOKING_LOG.map((line,i) => (
                  <div key={i} className={`acs-term-line${i<=(btLogTop%BOOKING_LOG.length)+3?' acs-tl-vis':''}`}
                    style={{color: i===btLogTop%BOOKING_LOG.length ? '#F5A523' : 'rgba(200,220,255,0.55)'}}>
                    <span className="acs-prompt" style={{color:'#F5A523'}}>›</span>
                    <span className="asr-log-time">[{line.time}]</span>
                    &nbsp;{line.msg}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
        <div className="awf-overlay">
          <div className="acs-dots">
            {[0,1,2,3].map(i=>(
              <button key={i} className={`acs-dot${slide===i?' acs-dot-active bt-dot-active':''}`} onClick={()=>setSlide(i)}/>
            ))}
          </div>
          <p className="awf-label" style={{color:'#F5A523'}}>{BT_CAPTIONS[slide].label}</p>
          <p className="awf-caption">{BT_CAPTIONS[slide].cap}</p>
        </div>
      </div>

      <div className="ai-showcase-right">
        <div className="asr-panel bt-asr-panel">
          <p className="awf-label" style={{color:'#F5A523',marginBottom:'18px'}}>APP STATS</p>
          {[['120+','Trucks Listed'],['45+','Routes Covered'],['3min','Avg. Booking Time'],['500+','Deliveries Made']].map(([val,lbl]) => (
            <div className="asr-stat" key={lbl}>
              <span className="asr-stat-val" style={{color:'#F5A523',textShadow:'0 0 12px rgba(245,165,35,0.45)'}}>{val}</span>
              <span className="asr-stat-lbl">{lbl}</span>
            </div>
          ))}
        </div>
        <div className="asr-panel bt-asr-panel">
          <p className="awf-label" style={{color:'#F5A523',marginBottom:'14px'}}>LIVE BOOKING FEED</p>
          <div className="asr-log">
            {visibleBtLog.map((entry,i) => (
              <div key={`${entry.time}-${i}`} className={`asr-log-row${i===0?' asr-log-new':''}`}
                style={i===0?{color:'#F5A523'}:{}}>
                <span className="asr-log-time">[{entry.time}]</span>
                <span className="asr-log-msg">{entry.msg}</span>
                <span className="asr-log-ok" style={{color:'#F5A523'}}>✓</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// === AI SHOWCASE ===
const TERM_LINES = [
  { p: '$', t: 'ai-engine --start --env production', c: 'cmd' },
  { p: '→', t: 'Connecting to data sources     [OK]', c: 'ok'   },
  { p: '→', t: 'Loading workflow definitions   [OK]', c: 'ok'   },
  { p: '·', t: 'Processing 1,247 records...        ', c: 'info' },
  { p: '!', t: 'Anomaly detected: invoice_847.pdf  ', c: 'warn' },
  { p: '→', t: 'Routing to approval queue      [OK]', c: 'ok'   },
  { p: '→', t: 'Notification dispatched        [OK]', c: 'ok'   },
  { p: '✓', t: 'Pipeline completed in 0.34s        ', c: 'suc'  },
  { p: '✓', t: 'Efficiency gain:          +847%    ', c: 'suc'  },
];

const LOG_ENTRIES = [
  { time: '09:42:01', msg: 'Invoice #847 processed'    },
  { time: '09:42:03', msg: 'Report generated & sent'   },
  { time: '09:42:05', msg: 'Alert dispatched to team'  },
  { time: '09:42:07', msg: 'CRM record auto-updated'   },
  { time: '09:42:09', msg: 'Approval workflow started' },
  { time: '09:42:11', msg: 'Backup task triggered'     },
  { time: '09:42:13', msg: 'Analytics pipeline done'   },
  { time: '09:42:15', msg: 'Email sequence scheduled'  },
];

const SLIDE_CAPTIONS = [
  { label: 'LIVE SESSION · 2026',      cap: 'Workflow Automation Using AI'        },
  { label: 'KEYNOTE · 2026',           cap: 'Scaling the AI-Integrated Enterprise'},
  { label: 'AI PIPELINE · ARCHITECTURE', cap: 'Intelligent Workflow Orchestration' },
  { label: 'LIVE AUTOMATION · OUTPUT', cap: 'AI Process Automation in Action'     },
];

const SVG_PATHS = [
  'M 96 80 C 180 80 180 150 228 150',
  'M 96 150 L 228 150',
  'M 96 220 C 180 220 180 150 228 150',
  'M 312 150 C 380 150 380 80 444 80',
  'M 312 150 L 444 150',
  'M 312 150 C 380 150 380 220 444 220',
];

function AIShowcase() {
  const [slide, setSlide] = useState(0);
  const [termIdx, setTermIdx] = useState(0);
  const [logTop, setLogTop] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % 4), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTermIdx(i => (i + 1) % TERM_LINES.length), 1300);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setLogTop(i => (i + 1) % LOG_ENTRIES.length), 1800);
    return () => clearInterval(t);
  }, []);

  const visibleLog = Array.from({ length: 5 }, (_, i) =>
    LOG_ENTRIES[(logTop + i) % LOG_ENTRIES.length]
  );

  return (
    <div className="ai-showcase-wrapper">
      {/* ── CAROUSEL ── */}
      <div className="ai-carousel-frame">
        <span className="awf-corner awf-tl"/><span className="awf-corner awf-tr"/>
        <span className="awf-corner awf-bl"/><span className="awf-corner awf-br"/>
        <div className="awf-scanline"/>

        <div className="acs-slides">
          {/* Slide 0 — workshop photo */}
          <div className={`acs-slide${slide === 0 ? ' acs-active' : ''}`}>
            <img src={aiWorkshopImg} alt="Robert Bota — Workflow Automation workshop" className="awf-image acs-photo"/>
          </div>
          {/* Slide 1 — conference photo */}
          <div className={`acs-slide${slide === 1 ? ' acs-active' : ''}`}>
            <img src={aiConferenceImg} alt="Robert Bota — Scaling the AI-Integrated Enterprise keynote" className="awf-image acs-photo"/>
          </div>
          {/* Slide 2 — animated node network */}
          <div className={`acs-slide acs-slide-dark${slide === 2 ? ' acs-active' : ''}`}>
            <svg className="acs-svg" viewBox="0 0 540 300" xmlns="http://www.w3.org/2000/svg">
              {SVG_PATHS.map((d, i) => (
                <g key={i}>
                  <path d={d} stroke="rgba(0,212,255,0.25)" strokeWidth="1.2" fill="none" strokeDasharray="6 3">
                    <animate attributeName="stroke-dashoffset" from="36" to="0" dur={`${1.1 + i * 0.15}s`} repeatCount="indefinite"/>
                  </path>
                  <circle r="3.5" fill="#00d4ff" opacity="0.9">
                    <animateMotion dur={`${1.3 + i * 0.18}s`} repeatCount="indefinite" path={d}/>
                  </circle>
                </g>
              ))}
              {[['TRIGGER', 70, 80], ['EMAIL', 70, 150], ['CRM', 70, 220]].map(([lbl, cx, cy]) => (
                <g key={lbl}>
                  <circle cx={cx} cy={cy} r="26" fill="rgba(0,8,20,0.9)" stroke="rgba(0,212,255,0.65)" strokeWidth="1.5"/>
                  <text x={cx} y={cy + 4} textAnchor="middle" fill="#00d4ff" fontSize="8" fontFamily="Orbitron,sans-serif" fontWeight="700">{lbl}</text>
                </g>
              ))}
              <circle cx="270" cy="150" r="52" fill="none" stroke="rgba(0,212,255,0.15)" strokeWidth="1">
                <animate attributeName="r" values="44;60;44" dur="2.6s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.5;0;0.5" dur="2.6s" repeatCount="indefinite"/>
              </circle>
              <circle cx="270" cy="150" r="44" fill="rgba(0,18,36,0.95)" stroke="rgba(0,212,255,0.95)" strokeWidth="2"/>
              <text x="270" y="146" textAnchor="middle" fill="#00d4ff" fontSize="8.5" fontFamily="Orbitron,sans-serif" fontWeight="900">AI</text>
              <text x="270" y="160" textAnchor="middle" fill="#00d4ff" fontSize="8.5" fontFamily="Orbitron,sans-serif" fontWeight="900">ENGINE</text>
              {[['NOTIFY', 470, 80], ['REPORT', 470, 150], ['EXECUTE', 470, 220]].map(([lbl, cx, cy]) => (
                <g key={lbl}>
                  <circle cx={cx} cy={cy} r="26" fill="rgba(0,8,20,0.9)" stroke="rgba(123,47,255,0.75)" strokeWidth="1.5"/>
                  <text x={cx} y={cy + 4} textAnchor="middle" fill="rgba(190,140,255,0.95)" fontSize="8" fontFamily="Orbitron,sans-serif" fontWeight="700">{lbl}</text>
                </g>
              ))}
            </svg>
          </div>
          {/* Slide 3 — terminal */}
          <div className={`acs-slide acs-slide-dark${slide === 3 ? ' acs-active' : ''}`}>
            <div className="acs-terminal">
              <div className="acs-term-hdr">
                <span className="acs-th-dot" style={{ background: '#E31837' }}/>
                <span className="acs-th-dot" style={{ background: '#F5A523' }}/>
                <span className="acs-th-dot" style={{ background: '#00d4ff' }}/>
                <span className="acs-th-ttl">automation-engine — process 14271</span>
              </div>
              <div className="acs-term-body">
                {TERM_LINES.map((line, i) => (
                  <div key={i} className={`acs-term-line acs-tc-${line.c}${i <= termIdx ? ' acs-tl-vis' : ''}`}>
                    <span className={`acs-prompt acs-tc-${line.c}`}>{line.p}</span>
                    {line.t}
                    {i === termIdx && <span className="acs-cursor">▋</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* caption + dot nav */}
        <div className="awf-overlay">
          <div className="acs-dots">
            {[0, 1, 2, 3].map(i => (
              <button key={i} className={`acs-dot${slide === i ? ' acs-dot-active' : ''}`} onClick={() => setSlide(i)}/>
            ))}
          </div>
          <p className="awf-label">{SLIDE_CAPTIONS[slide].label}</p>
          <p className="awf-caption">{SLIDE_CAPTIONS[slide].cap}</p>
        </div>
      </div>

      {/* ── RIGHT PANELS ── */}
      <div className="ai-showcase-right">
        <div className="asr-panel">
          <p className="awf-label" style={{ marginBottom: '18px' }}>IMPACT METRICS</p>
          {[
            ['12+',   'Workshops Delivered'],
            ['180+',  'Professionals Trained'],
            ['47+',   'Workflows Automated'],
            ['+340%', 'Avg. Efficiency Gain'],
          ].map(([val, lbl]) => (
            <div className="asr-stat" key={lbl}>
              <span className="asr-stat-val">{val}</span>
              <span className="asr-stat-lbl">{lbl}</span>
            </div>
          ))}
        </div>

        <div className="asr-panel">
          <p className="awf-label" style={{ marginBottom: '14px' }}>LIVE PROCESS LOG</p>
          <div className="asr-log">
            {visibleLog.map((entry, i) => (
              <div key={`${entry.time}-${i}`} className={`asr-log-row${i === 0 ? ' asr-log-new' : ''}`}>
                <span className="asr-log-time">[{entry.time}]</span>
                <span className="asr-log-msg">{entry.msg}</span>
                <span className="asr-log-ok">✓</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// === THE HOME PAGE COMPONENT ===
function Home() {
  const [click, setClick] = useState(false);
  const handleClick = () => setClick(!click);
  const closeMobileMenu = () => setClick(false);
  const [textIndex, setTextIndex] = useState(0);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTextIndex((prevIndex) => (prevIndex + 1) % rotatingItems.length);
    }, 3800);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const sections = ['home', 'about', 'subsidiaries', 'booktruck', 'ai-solutions', 'contact'];
    const onScroll = () => {
      const midpoint = window.scrollY + window.innerHeight * 0.35;
      let current = 'home';
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= midpoint) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const whatsAppLink = "https://wa.me/263774790252";

  return (
      <>
        <nav className="navbar">
          <div className="navbar-container container">
            <a href="#home" className="navbar-logo" onClick={closeMobileMenu}>
              <img src={logo} alt="StopNext Services Logo" />
            </a>
            <div className="menu-icon" onClick={handleClick}>
              {click ? <FaTimes /> : <FaBars />}
            </div>
            <ul className={click ? 'nav-menu active' : 'nav-menu'}>
              <li className="nav-item"><a href="#home" className={`nav-links${activeSection==='home'?' nav-active':''}`} onClick={closeMobileMenu}>Home</a></li>
              <li className="nav-item"><a href="#about" className={`nav-links${activeSection==='about'?' nav-active':''}`} onClick={closeMobileMenu}>About Us</a></li>
              <li className="nav-item"><a href="#subsidiaries" className={`nav-links${activeSection==='subsidiaries'?' nav-active':''}`} onClick={closeMobileMenu}>Our Services</a></li>
              <li className="nav-item"><a href="#booktruck" className={`nav-links${activeSection==='booktruck'?' nav-active':''}`} onClick={closeMobileMenu}>Our Products</a></li>
              <li className="nav-item"><a href="#ai-solutions" className={`nav-links${activeSection==='ai-solutions'?' nav-active':''}`} onClick={closeMobileMenu}>AI Solutions</a></li>
              <li className="nav-item"><a href="#contact" className={`nav-links${activeSection==='contact'?' nav-active':''}`} onClick={closeMobileMenu}>Contact</a></li>
              <li className="nav-item">
                <a href={whatsAppLink} target="_blank" rel="noopener noreferrer" className="btn btn-red" style={{marginTop: '20px', marginLeft: '20px'}} onClick={closeMobileMenu}>
                  Chat on WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </nav>

        <section id="home" className="hero-container">
          <p className="hero-eyebrow">Est. 2026 · Zimbabwe</p>
          <h1>StopNext Services</h1>
          <span className="hero-pvt">Pvt Ltd</span>
          <div className="hero-motto">
            <span className="motto-dash" />
            <span className="motto-text">Growth Without Limits</span>
            <span className="motto-dash" />
          </div>
          <div className="hero-divider"></div>

          {/* === FUTURISTIC DISPLAY PANEL === */}
          <div className="hero-display-panel">
            <span className="dp-corner dp-tl" />
            <span className="dp-corner dp-tr" />
            <span className="dp-corner dp-bl" />
            <span className="dp-corner dp-br" />

            <div className="dp-header">
              <span className="dp-category">
                <span className="dp-arrow">▸</span> {rotatingItems[textIndex].category}
              </span>
              <span className="dp-counter">
                {String(textIndex + 1).padStart(2, '0')} / {String(rotatingItems.length).padStart(2, '0')}
              </span>
            </div>

            <div className="dp-body">
              <ScrambleText key={textIndex} text={rotatingItems[textIndex].text} />
              <span className="dp-cursor">_</span>
            </div>

            <div className="dp-footer">
              {rotatingItems.map((_, i) => (
                <span key={i} className={`dp-dot${i === textIndex ? ' dp-dot-active' : ''}`} />
              ))}
            </div>
          </div>

          <p className="hero-sub">A diversified holding company engineering progress in transport, technology, construction, and intelligence.</p>
          <div className="hero-btns">
            <a href="#subsidiaries" className="btn btn-red">Our Divisions</a>
            <a href="#ai-solutions" className="btn-ai" style={{padding:'13px 32px'}}>AI Solutions</a>
          </div>
        </section>

        <section id="about" className="about-section">
          <div className="container">
            <h2 className="section-title">About The Company</h2>
            <AnimateIn className="about-content">
              <p>StopNext Services (Pvt) Ltd is a forward-thinking Zimbabwean holding company established in 2026, operating at the intersection of transport, technology, construction, and artificial intelligence. We build the infrastructure, software, and structures that move people, data, and businesses forward.</p>
            </AnimateIn>
            <div className="about-stats">
              <AnimateIn className="stat-item" delay={0}>
                <CountUp value="2026" className="stat-number" />
                <span className="stat-label">Established</span>
              </AnimateIn>
              <AnimateIn className="stat-item" delay={100}>
                <CountUp value="3" className="stat-number" />
                <span className="stat-label">Divisions</span>
              </AnimateIn>
              <AnimateIn className="stat-item" delay={200}>
                <CountUp value="14+" className="stat-number" />
                <span className="stat-label">Services</span>
              </AnimateIn>
              <AnimateIn className="stat-item" delay={300}>
                <CountUp value="ZWE" className="stat-number" />
                <span className="stat-label">Operating In</span>
              </AnimateIn>
            </div>
          </div>
        </section>

        <section id="subsidiaries" className="services-section">
          <div className="container">
            <h2 className="section-title">Our Divisions</h2>
            <div className="subsidiary-group">
              <AnimateIn className="subsidiary-intro">
                <h3 className="subsidiary-name">StopNext Logistics & Transport</h3>
                <p>Reliable mobility solutions for individuals and businesses across Zimbabwe.</p>
              </AnimateIn>
              <div className="services-wrapper">
                <AnimateIn className="service-card" delay={0}>
                  <div className="service-icon"><FaTaxi /></div>
                  <h3>Taxi Rides</h3>
                  <p>Safe, reliable, and convenient private taxi services for individuals and corporates.</p>
                </AnimateIn>
                <AnimateIn className="service-card" delay={100}>
                  <div className="service-icon"><FaBus /></div>
                  <h3>Shuttles & Kombis</h3>
                  <p>Efficient commuter shuttles and kombi services connecting people across the city.</p>
                </AnimateIn>
                <AnimateIn className="service-card" delay={200}>
                  <div className="service-icon"><FaMotorcycle /></div>
                  <h3>Motorbike Delivery</h3>
                  <p>Fast, agile last-mile delivery using motorbikes — parcels, packages, and documents delivered swiftly.</p>
                </AnimateIn>
                <AnimateIn className="service-card" delay={300}>
                  <div className="service-icon"><FaBoxOpen /></div>
                  <h3>Courier & Logistics</h3>
                  <p>End-to-end courier solutions for businesses — scheduled pickups, tracked delivery, and reliable fulfilment.</p>
                </AnimateIn>
              </div>
              <LogisticsShowcase />
            </div>
            <div className="subsidiary-divider"></div>
            <div className="subsidiary-group">
              <div className="subsidiary-intro">
                <h3 className="subsidiary-name">StopNext Technologies</h3>
                <p>Full-spectrum software engineering and digital transformation for modern businesses.</p>
              </div>
              <div className="services-wrapper">
                <AnimateIn className="service-card" delay={0}><div className="service-icon"><FaGlobe /></div><h3>Website Design</h3><p>Modern, responsive, and high-converting websites built for results.</p></AnimateIn>
                <AnimateIn className="service-card" delay={100}><div className="service-icon"><FaMobileAlt /></div><h3>Mobile App Development</h3><p>Native and cross-platform apps for iOS and Android, from concept to launch.</p></AnimateIn>
                <AnimateIn className="service-card" delay={200}><div className="service-icon"><FaLaptopCode /></div><h3>Custom Software Solutions</h3><p>Bespoke enterprise-grade software engineered precisely for your workflows.</p></AnimateIn>
                <AnimateIn className="service-card" delay={300}><div className="service-icon"><FaPlug /></div><h3>API Integration</h3><p>Seamlessly connect your systems with third-party APIs and external platforms.</p></AnimateIn>
                <AnimateIn className="service-card" delay={400}><div className="service-icon"><FaNetworkWired /></div><h3>Enterprise Integration</h3><p>Unified data flows across ERP, CRM, and legacy systems — zero data silos.</p></AnimateIn>
                <AnimateIn className="service-card" delay={500}><div className="service-icon"><FaProjectDiagram /></div><h3>System Design & Architecture</h3><p>Scalable, secure system blueprints engineered for long-term growth.</p></AnimateIn>
              </div>
              <TechShowcase />
            </div>
            <div className="subsidiary-divider"></div>
            <div className="subsidiary-group">
              <AnimateIn className="subsidiary-intro">
                <h3 className="subsidiary-name" style={{color:'#10B981'}}>StopNext Construction</h3>
                <p>Quality construction, tiling, ceilings, and renovations — built to last and finished to impress.</p>
              </AnimateIn>
              <div className="services-wrapper">
                <AnimateIn className="service-card cn-service-card" delay={0}>
                  <div className="service-icon cn-service-icon"><FaHardHat /></div>
                  <h3>House Construction</h3>
                  <p>From foundation to finish — we build solid, beautiful homes across Zimbabwe.</p>
                </AnimateIn>
                <AnimateIn className="service-card cn-service-card" delay={100}>
                  <div className="service-icon cn-service-icon"><FaTh /></div>
                  <h3>Professional Tiling</h3>
                  <p>Flawless floors and walls. Precision tiling for kitchens, bathrooms, patios, and commercial spaces.</p>
                </AnimateIn>
                <AnimateIn className="service-card cn-service-card" delay={200}>
                  <div className="service-icon cn-service-icon"><FaLayerGroup /></div>
                  <h3>Modern Ceilings</h3>
                  <p>Elegant, stylish ceiling designs — suspended, gypsum, coffered — elevating every interior space.</p>
                </AnimateIn>
                <AnimateIn className="service-card cn-service-card" delay={300}>
                  <div className="service-icon cn-service-icon"><FaPaintRoller /></div>
                  <h3>Renovations & Painting</h3>
                  <p>Refreshing your current space with expert renovations, interior & exterior painting, and finishing.</p>
                </AnimateIn>
              </div>
              <ConstructionShowcase />
            </div>
          </div>
        </section>

        {/* === BOOKTRUCK PRODUCT SPOTLIGHT === */}
        <section id="booktruck" className="booktruck-section">
          <div className="container">
            <div className="booktruck-grid">

              <AnimateIn className="bt-icon-side">
                <div className="bt-icon-wrapper">
                  <img src={booktruckIcon} alt="BookTruck App Icon" className="bt-icon" />
                </div>
              </AnimateIn>

              <AnimateIn className="bt-content-side" delay={160}>
                <p className="ai-eyebrow">OUR PRODUCT</p>
                <div className="bt-title-row">
                  <h2 className="bt-title">BookTruck</h2>
                  <span className="bt-badge">BETA</span>
                </div>
                <p className="bt-store-label">
                  <FaGooglePlay className="bt-store-icon" /> Available on Google Play · Closed Testing
                </p>
                <p className="bt-desc">
                  BookTruck connects businesses and individuals with verified truck drivers across Zimbabwe.
                  Book a truck in minutes, track your shipment in real-time, and move cargo with complete confidence.
                </p>
                <div className="bt-features">
                  <span className="bt-chip"><FaMapPin /> Real-Time GPS Tracking</span>
                  <span className="bt-chip"><FaShieldAlt /> Verified Drivers</span>
                  <span className="bt-chip"><FaMobileAlt /> Instant Booking</span>
                  <span className="bt-chip"><FaBell /> Live Shipment Updates</span>
                </div>
                <a href="#contact" className="bt-cta-btn">
                  Request Early Access <FaArrowRight />
                </a>
              </AnimateIn>

            </div>
            <BookTruckShowcase />
          </div>
        </section>

        <section id="ai-solutions" className="ai-section">
          <div className="container">
            <p className="ai-eyebrow">THE FUTURE IS NOW</p>
            <h2 className="ai-section-title">AI-Powered Solutions</h2>
            <p className="ai-section-subtitle">
              We bring artificial intelligence from concept to production — for businesses of every size.
            </p>
            <div className="services-wrapper">
              <AnimateIn className="ai-card" delay={0}>
                <div className="ai-card-icon"><FaRobot /></div>
                <h3>Workflow Automation</h3>
                <p>End-to-end automation of repetitive business processes using intelligent AI pipelines — freeing your team to focus on what matters.</p>
              </AnimateIn>
              <AnimateIn className="ai-card" delay={120}>
                <div className="ai-card-icon"><FaBrain /></div>
                <h3>AI Strategy & Consulting</h3>
                <p>We identify where AI creates the most value in your business and build a clear, actionable roadmap to get you there.</p>
              </AnimateIn>
              <AnimateIn className="ai-card" delay={240}>
                <div className="ai-card-icon"><FaChalkboardTeacher /></div>
                <h3>AI Workshops for Teams</h3>
                <p>Practical, hands-on training sessions so your employees can confidently use AI tools in their day-to-day work.</p>
              </AnimateIn>
              <AnimateIn className="ai-card" delay={360}>
                <div className="ai-card-icon"><FaCogs /></div>
                <h3>Custom AI Integration</h3>
                <p>Embed AI capabilities directly into your existing software, data pipelines, and business workflows — without starting from scratch.</p>
              </AnimateIn>
            </div>
            <AIShowcase />
            <div className="ai-cta-wrapper">
              <a href="#contact" className="btn-ai">Request AI Consultation</a>
            </div>
          </div>
        </section>

        {/* === TESTIMONIALS SECTION === */}
        <section className="testimonials-section">
          <div className="container">
            <p className="ai-eyebrow">CLIENT FEEDBACK</p>
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="testimonials-sub">Hear from businesses we have helped embrace AI and automation.</p>
            <div className="testimonials-wrapper">
              {testimonials.map((t, i) => (
                <AnimateIn key={i} className="testimonial-card" delay={i * 150}>
                  <FaQuoteLeft className="t-quote-icon" />
                  <p className="t-text">{t.text}</p>
                  <div className="t-stars">
                    {[...Array(5)].map((_, s) => <FaStar key={s} className="t-star" />)}
                  </div>
                  <div className="t-author">
                    <div className="t-avatar">{t.initials}</div>
                    <div className="t-author-info">
                      <span className="t-name">{t.name}</span>
                      <span className="t-role">{t.role} · {t.company}</span>
                    </div>
                  </div>
                </AnimateIn>
              ))}
            </div>
          </div>
        </section>

        {/* === CONTACT FORM SECTION === */}
        <section id="contact" className="contact-section">
          <div className="container">
            <p className="ai-eyebrow">GET IN TOUCH</p>
            <h2 className="section-title">Send A Message</h2>
            <p className="contact-section-sub">Have a project in mind or need a quote? Fill in the form and we'll respond within 24 hours.</p>
            <AnimateIn className="contact-form-panel">
              <span className="cfp-corner cfp-tl" /><span className="cfp-corner cfp-tr" />
              <span className="cfp-corner cfp-bl" /><span className="cfp-corner cfp-br" />
              <ContactForm />
            </AnimateIn>
          </div>
        </section>

        <footer className="footer-container">
          <div className="footer-grid-overlay" />

          {/* Connection header */}
          <AnimateIn className="footer-connect-header">
            <p className="footer-eyebrow-label">▸ ESTABLISH CONNECTION</p>
            <h2 className="footer-connect-title">Get In Touch</h2>
            <p className="footer-connect-sub">We are always operational — reach us through any channel below.</p>
            <div className="footer-header-divider" />
          </AnimateIn>

          <div className="footer-wrapper">
            {/* Quick Links */}
            <div className="footer-link-items">
              <h2>Quick Links</h2>
              <a href="#home">Home</a>
              <a href="#about">About</a>
              <a href="#subsidiaries">Divisions</a>
              <a href="#ai-solutions">AI Solutions</a>
              <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/privacy">BookTruck App Privacy</Link>
            </div>

            {/* Contact panel */}
            <div className="footer-link-items footer-contact-panel">
              <span className="fcp-corner fcp-tl" /><span className="fcp-corner fcp-tr" />
              <span className="fcp-corner fcp-bl" /><span className="fcp-corner fcp-br" />
              <h2>Contact Us</h2>
              <a href="tel:+263774790252" className="contact-info-item">
                <FaPhoneAlt className="ci-icon" /> +263 774 790 252
              </a>
              <a href="tel:+263787270066" className="contact-info-item">
                <FaPhoneAlt className="ci-icon" /> +263 787 270 066
              </a>
              <a href="mailto:info@stopnextservices.co.zw" className="contact-info-item">
                <FaEnvelope className="ci-icon" /> info@stopnextservices.co.zw
              </a>
              <div className="contact-info-item">
                <FaMapMarkerAlt className="ci-icon" /> 776 Olive Street, Sunway City, Harare
              </div>
              <div className="contact-info-item">
                <FaShieldAlt className="ci-icon" /> Reg No. 88750A0232026
              </div>
            </div>

            {/* Social */}
            <div className="footer-link-items">
              <h2>Social Media</h2>
              <a href="https://www.facebook.com/stopnextservices" target="_blank" rel="noopener noreferrer" className="contact-info-item">
                <FaFacebook className="ci-icon" /> Facebook
              </a>
              <a href="https://www.linkedin.com/company/stopnext-services-pvt-ltd/" target="_blank" rel="noopener noreferrer" className="contact-info-item">
                <FaLinkedin className="ci-icon" /> LinkedIn
              </a>
            </div>
          </div>

          <section className="social-media-wrap">
            <small className="website-rights">© {new Date().getFullYear()} StopNext Services (Pvt) Ltd. All rights reserved.</small>
          </section>
        </footer>
      </>
  );
}

// === COOKIE CONSENT BANNER ===
function CookieBanner({ onAccept }) {
  return (
    <div className="cookie-banner">
      <div className="cookie-content">
        <p>
          We use cookies to improve your experience and power our live chat.
          By continuing you accept our{' '}
          <Link to="/privacy-policy" className="cookie-link">Privacy Policy</Link>.
        </p>
        <div className="cookie-actions">
          <button className="cookie-btn-accept" onClick={onAccept}>Accept</button>
        </div>
      </div>
    </div>
  );
}

// === MAIN APP WRAPPER ===
function App() {
  const [cookieAccepted, setCookieAccepted] = useState(
    () => localStorage.getItem('sn_cookie_consent') === 'yes'
  );

  const handleAccept = () => {
    localStorage.setItem('sn_cookie_consent', 'yes');
    setCookieAccepted(true);
  };

  // Only load Tawk.to after consent
  useEffect(() => {
    if (!cookieAccepted) return;
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    (function() {
      var s1 = document.createElement("script"), s0 = document.getElementsByTagName("script")[0];
      s1.async = true;
      s1.src = 'https://embed.tawk.to/69735e76f766c1197dd15c22/1jflai003';
      s1.charset = 'UTF-8';
      s1.setAttribute('crossorigin', '*');
      s0.parentNode.insertBefore(s1, s0);
    })();
  }, [cookieAccepted]);

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/privacy-policy" element={<GeneralPrivacy />} />
          <Route path="/privacy" element={<Privacy />} />
        </Routes>
        {!cookieAccepted && <CookieBanner onAccept={handleAccept} />}
      </div>
    </Router>
  );
}

export default App;